import createFetchClient, { type Client } from 'openapi-fetch';
import createQueryHooks, { type OpenapiQueryClient } from 'openapi-react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { ApiError, responseError } from './errors';
import type { paths } from './schema';

export type Panel = 'merchant' | 'admin';

// How a request carries its credentials (UI architecture §6; §8 seam 3). The default, the session
// cookie, adds no header; a later token login for a native app adds its own.
export type AuthTransport = { headers(): Record<string, string> };

export type ApiClientOptions = {
  baseUrl: string;
  panel: Panel;
  authTransport?: AuthTransport;
  onUnauthenticated?: () => void;
};

export type ApiClient = {
  fetchClient: Client<paths>;
  $api: OpenapiQueryClient<paths>;
  panel: Panel;
  // The app's handler for a lost session; /auth calls it for a /me read that loses one.
  onUnauthenticated: () => void;
};

const mutatingMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// A 401 from these means "not logged in" or a session already gone, not a session lost on the way
// (UI architecture §6).
const sessionCalls = new Set([
  'GET /api/me',
  'GET /admin/api/me',
  'POST /api/auth/logout',
  'POST /admin/api/auth/logout',
]);

// A digest, not the body itself: an action's entry stays in memory until the action succeeds.
async function bodyHash(request: Request): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await request.clone().arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// The only code that touches credentials (UI architecture §6): every request goes with the cookie,
// plus the headers of the auth transport.
export function createApiClient(options: ApiClientOptions): ApiClient {
  const onUnauthenticated = options.onUnauthenticated ?? (() => undefined);
  const fetchClient = createFetchClient<paths>({
    baseUrl: options.baseUrl,
    credentials: 'include',
  });
  // One Idempotency-Key per user action (UI architecture §4; D-057): the same method, URL and body
  // keep their key until they get a final answer, so "try again" replays; any other action, and
  // the same body after a final answer, gets a new key (Merchant API §1.6).
  const idempotencyKeys = new Map<string, string>();
  fetchClient.use({
    async onRequest({ request }) {
      for (const [name, value] of Object.entries(options.authTransport?.headers() ?? {})) {
        request.headers.set(name, value);
      }
      if (!mutatingMethods.has(request.method)) return;
      const action = `${request.method} ${request.url} ${await bodyHash(request)}`;
      const key = idempotencyKeys.get(action) ?? crypto.randomUUID();
      idempotencyKeys.set(action, key);
      request.headers.set('Idempotency-Key', key);
    },
    async onResponse({ request, response, schemaPath }) {
      const error = response.ok ? null : await responseError(response);
      // "Try again" keeps the key after a 5xx and after a 409 for the first request still in flight
      // (a network failure never gets here and keeps it too); every other answer is final.
      const unfinished =
        error !== null &&
        (error.status >= 500 || (error.type === 'conflict' && error.retryAfterS !== null));
      if (!unfinished) {
        const key = request.headers.get('Idempotency-Key');
        for (const [action, value] of idempotencyKeys) {
          if (value === key) idempotencyKeys.delete(action);
        }
      }
      if (error === null) return;
      const sessionCall = sessionCalls.has(`${request.method} ${schemaPath}`);
      if (error.type === 'unauthenticated' && !sessionCall) onUnauthenticated();
      throw error;
    },
    onError() {
      return new ApiError({ status: 0, type: null, details: [], retryAfterS: null, traceId: null });
    },
  });
  return {
    fetchClient,
    $api: createQueryHooks(fetchClient),
    panel: options.panel,
    onUnauthenticated,
  };
}

const ApiContext = createContext<ApiClient | null>(null);

// Hands the app's client to the screens: configuration through providers (UI architecture §15).
export function ApiProvider({ client, children }: { client: ApiClient; children: ReactNode }) {
  return <ApiContext value={client}>{children}</ApiContext>;
}

export function useApiClient(): ApiClient {
  const client = useContext(ApiContext);
  if (client === null) throw new Error('useApiClient() needs an <ApiProvider> above it.');
  return client;
}
